import { TableLocal, TableLocalPagination } from "components/Common/TableLocal"
import { useListParams } from "hooks/useListParams"
import { useCallback, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"

const QuotationList = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [sortOrder, setSortOrder] = useState("desc")

  const {
    params,
    setPage,
    setSearch,
    resetParams,
    hasActiveFilters,
    updateParams,
  } = useListParams({
    page: 1,
    search: "",
  })

  // Sort contents by date
  const sortedContents = useMemo(() => {
    if (!data) return []
    const contents = Array.isArray(data) ? data : []

    return [...contents].sort((a, b) => {
      const dateA = new Date(a.created_at)
      const dateB = new Date(b.created_at)

      if (sortOrder === "desc") {
        return dateB - dateA
      } else {
        return dateA - dateB
      }
    })
  }, [data, sortOrder])

  const toggleSortOrder = useCallback(() => {
    setSortOrder(prev => (prev === "desc" ? "asc" : "desc"))
    setCurrentPage(1)
    updateParams(prev => ({
      ...prev,
      p_page: 1,
    }))
  }, [updateParams])

  const handlePageChange = useCallback(
    page => {
      setCurrentPage(page)
      updateParams(prev => ({
        ...prev,
        p_page: page,
      }))
    },
    [updateParams]
  )

  const handleItemsPerPageChange = useCallback(
    newItemsPerPage => {
      setItemsPerPage(newItemsPerPage)
      setCurrentPage(1)
      updateParams(prev => ({
        ...prev,
        p_page: 1,
      }))
    },
    [updateParams]
  )

  const getVehicleInfo = (_data, fieldName) => {
    return _data?.find(v => v.key === fieldName)?.value ?? "-"
  }

  // Static columns
  const staticColumns = useMemo(
    () => [
      {
        header: "Vehicle Information",
        cell: row => {
          const vehicleSpec = row.pdf_content?.vehicle_specs || []
          return (
            <div className="text-start">
              {/* Header with QID and Status */}
              <div className="d-flex align-items-start justify-content-between mb-2">
                <span className="fw-bold text-primary fs-6">
                  <Link to={`/quotation-list/${row.qid}/view`}>{row.qid}</Link>
                </span>
                <span
                  className={`badge ${
                    row.status?.slug === "pending"
                      ? "bg-warning-subtle text-warning-emphasis"
                      : row.status?.slug === "approved"
                      ? "bg-success-subtle text-success-emphasis"
                      : "bg-secondary-subtle text-secondary-emphasis"
                  } px-2 py-1`}
                >
                  {row.status?.name}
                </span>
              </div>

              {/* Vehicle Name */}
              <div className="fw-semibold text-dark mb-1 fs-6">
                {getVehicleInfo(vehicleSpec, "Vehicle Name")}
              </div>

              {/* Vehicle Details */}
              <div className="d-flex flex-wrap gap-2 mb-2">
                <span className="badge bg-light text-dark border">
                  {getVehicleInfo(vehicleSpec, "Model")}
                </span>
                <span className="badge bg-light text-dark border">
                  {getVehicleInfo(vehicleSpec, "Color")}
                </span>
                <span className="badge bg-light text-dark border">
                  {getVehicleInfo(vehicleSpec, "CC")} CC
                </span>
                <span className="badge bg-light text-dark border">
                  {getVehicleInfo(vehicleSpec, "Fuel Type")}
                </span>
              </div>

              {/* Technical Details */}
              <div className="text-muted small">
                <div className="d-flex gap-3">
                  <span title="Stock ID" className="text-primary fw-bold">
                    <i className="bx bx-barcode me-1"></i>
                    <Link
                      to={`/stock-list/${row?.vehicle?.stock_id ?? ""}/view`}
                    >
                      {row?.vehicle?.stock_id ?? ""}
                    </Link>
                  </span>
                  <span title="Chassis No">
                    <i className="bx bx-barcode me-1"></i>
                    {getVehicleInfo(vehicleSpec, "Chassis No")}
                  </span>
                  <span title="Engine No">
                    <i className="bx bx-cog me-1"></i>
                    {getVehicleInfo(vehicleSpec, "Engine No") || "N/A"}
                  </span>
                </div>
              </div>

              {/* Generated Info */}
              <div className="border-top pt-2">
                <div className="small text-muted mb-1">
                  <i className="bx bx-user-circle me-1"></i>
                  <span className="fw-medium">Generated By:</span>
                </div>
                <div className="small text-dark">
                  {row?.pdf_content?.signatory_name}
                </div>
                <div className="small text-muted">
                  {row?.pdf_content?.signatory_designation}
                </div>
              </div>
            </div>
          )
        },
      },
      {
        header: "Financial Details",
        cell: row => (
          <div className="text-start">
            {/* Price Breakdown */}
            <div className="mb-2">
              {row.pdf_content?.price_items?.map(item => (
                <div
                  key={item?.id}
                  className="d-flex justify-content-between align-items-center small mb-1"
                >
                  <span className="text-muted">{item?.description}:</span>
                  <span className="fw-medium">{formatBDT(item?.amount)}</span>
                </div>
              ))}
            </div>

            {/* Total Amount */}
            <div className="border-top pt-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-bold text-dark">Total Amount:</span>
                <span className="fw-bold text-success fs-5">
                  {formatBDT(row.pdf_content?.total_amount)}
                </span>
              </div>

              {/* Amount in Words */}
              <div
                className="text-muted small lh-sm"
                style={{ maxWidth: "200px" }}
              >
                <i className="bx bx-text me-1"></i>
                {row.pdf_content?.amount_in_words}
              </div>
            </div>
          </div>
        ),
      },
      {
        header: "Actions",
        cell: row => (
          <div className="d-flex flex-column gap-2">
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center gap-1"
              title="View Quotation"
            >
              <i className="bx bx-show fs-6"></i>
              <span>View</span>
            </button>
            <button
              className="btn btn-outline-dark btn-sm d-flex align-items-center justify-content-center gap-1"
              title="Download PDF"
            >
              <i className="bx bx-download fs-6"></i>
              <span>Download</span>
            </button>
            <button
              className="btn btn-outline-warning btn-sm d-flex align-items-center justify-content-center gap-1"
              title="Edit Quotation"
            >
              <i className="bx bx-edit fs-6"></i>
              <span>Edit</span>
            </button>
          </div>
        ),
      },
    ],
    []
  )

  // Dynamic date column
  const dateColumn = useMemo(
    () => ({
      header: (
        <div
          className="d-flex align-items-center gap-1 cursor-pointer text-decoration-none"
          onClick={toggleSortOrder}
          style={{ cursor: "pointer" }}
        >
          <span className="fw-medium">Date Information</span>
          <i
            className={`bx bx-sort-${
              sortOrder === "desc" ? "down" : "up"
            } fs-5`}
          />
        </div>
      ),
      cell: row => (
        <div className="text-start">
          {/* Quotation Date */}
          <div className="mb-2">
            <div className="d-flex align-items-center gap-1 text-success mb-1">
              <i className="bx bx-calendar-check fs-5"></i>
              <span className="fw-semibold">Quotation Date</span>
            </div>
            <div className="small text-dark">
              {formatReadableDate(
                row.pdf_content?.quotation_date,
                "EEE, MMM d, y"
              )}
            </div>
          </div>

          {/* Valid Until */}
          <div className="mb-2">
            <div className="d-flex align-items-center gap-1 text-danger mb-1">
              <i className="bx bx-time-five fs-5"></i>
              <span className="fw-semibold">Valid Until</span>
            </div>
            <div className="small text-dark">
              {formatReadableDate(
                row.pdf_content?.valid_until,
                "EEE, MMM d, y"
              )}
            </div>
          </div>

          {/* System Info */}
          <div className="border-top pt-2 mt-2">
            <div className="small text-muted">
              <i className="bx bx-history me-1"></i>
              Created:{" "}
              {formatReadableDate(row.created_at, "MMM d, yyyy 'at' h:mm a")}
            </div>
          </div>
        </div>
      ),
    }),
    [toggleSortOrder, sortOrder]
  )

  // Combine columns with date in the middle for better flow
  const columns = useMemo(() => {
    const cols = [...staticColumns]
    cols.splice(2, 0, dateColumn) // Insert date column after Vehicle info
    return cols
  }, [staticColumns, dateColumn])

  return (
    <>
      <TableLocal
        columns={columns}
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        tableClass="align-middle table-hover mb-0"
        emptyMessage={
          <div className="text-center py-5">
            <i className="bx bx-file bx-3x text-muted mb-3"></i>
            <h5 className="text-muted">No quotations found</h5>
            <p className="text-muted mb-0">
              Create your first quotation to get started
            </p>
          </div>
        }
      />

      <TableLocalPagination
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        showItemsPerPage={true}
        itemsPerPageOptions={[5, 10, 20]}
        className="px-3 py-3 border-top"
      />
    </>
  )
}

export default QuotationList
